import { toast } from "./toast";
import { showPaymentSuccess } from "./paymentSuccess";
import axios from "axios";
import { hostUrl } from "./globals";
import { postRequestAuthenticated } from "../services/rest";

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const existingScript = document.querySelector(
      'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
    );
    if (existingScript) {
      resolve(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

// Publishable Razorpay key. Staging sets REACT_APP_RAZORPAY_KEY to a test key
// (rzp_test_...) so checkout never charges real money; prod falls back to live.
const RAZORPAY_KEY =
  process.env.REACT_APP_RAZORPAY_KEY || "rzp_live_w50CppHJGDmkhe";

// data: { amount, currency, planId, billingCycle, planName }
export const handlePayment = async (myData, data) => {
  const res = await loadRazorpayScript();
  if (!res) {
    toast("Razorpay SDK failed to load. Are you online?");
    return;
  }

  try {
    // 1) create a Razorpay order on the backend (adds 18% GST for subscriptions)
    const order = await postRequestAuthenticated("/create/transaction/order", {
      amount: data.amount,
      currency: data.currency || "INR",
      planId: data.planId,
      billingCycle: data.billingCycle,
      type: "subscription",
    });
    if (!order || !order.success || !order.id) {
      toast((order && order.message) || "Could not start checkout. Try again.");
      return;
    }

    // 2) open Razorpay checkout for that order
    const options = {
      key: RAZORPAY_KEY,
      amount: order.amount,
      currency: order.currency,
      name: "Channels.social",
      description: `Subscription — ${data.planName || data.planId} plan`,
      image: "https://cdn.channels.social/channels_logo.svg",
      order_id: order.id,
      handler: async function (response) {
        // 3) verify the payment server-side -> sets the subscription
        const verification = await postRequestAuthenticated("/verify/payment", {
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
        });
        if (verification && verification.status === "success") {
          // Animated success dialog, then head to the overview/dashboard.
          showPaymentSuccess({
            onDone: () => {
              window.location.href = "/admin/dashboard";
            },
          });
        } else {
          toast((verification && verification.message) || "Payment verification failed.");
        }
      },
      prefill: {
        name: myData?.fullName || myData?.username,
        email: myData?.email,
        contact: myData?.contact || myData?.whatsapp_number || "",
      },
      theme: { color: "#202020" },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  } catch (error) {
    console.error("checkout error", error);
    toast("Checkout failed. Please try again.");
  }
};

// Recurring monthly mandate (Razorpay Subscription). data: { planId,
// billingCycle, planName }. Amount comes from the seeded Razorpay Plan, so we
// don't pass it here.
export const handleSubscription = async (myData, data) => {
  const res = await loadRazorpayScript();
  if (!res) {
    toast("Razorpay SDK failed to load. Are you online?");
    return;
  }

  try {
    // 1) create the subscription (mandate) on the backend
    const sub = await postRequestAuthenticated("/create/subscription", {
      planId: data.planId,
      billingCycle: data.billingCycle || "monthly",
    });
    if (!sub || !sub.success || !sub.subscriptionId) {
      toast((sub && sub.message) || "Could not start checkout. Try again.");
      return;
    }

    // 2) open Razorpay checkout for the subscription (sets up auto-pay)
    const options = {
      key: RAZORPAY_KEY,
      subscription_id: sub.subscriptionId,
      name: "Channels.social",
      description: `${data.planName || data.planId} plan — monthly`,
      image: "https://cdn.channels.social/channels_logo.svg",
      handler: async function (response) {
        // 3) verify server-side (subscription signature) -> activates the plan
        const verification = await postRequestAuthenticated(
          "/verify/subscription",
          {
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_subscription_id: response.razorpay_subscription_id,
            razorpay_signature: response.razorpay_signature,
          }
        );
        if (verification && verification.status === "success") {
          showPaymentSuccess({
            onDone: () => {
              window.location.href = "/admin/dashboard";
            },
          });
        } else {
          toast(
            (verification && verification.message) ||
              "Payment verification failed."
          );
        }
      },
      prefill: {
        name: myData?.fullName || myData?.username,
        email: myData?.email,
        contact: myData?.contact || myData?.whatsapp_number || "",
      },
      theme: { color: "#202020" },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  } catch (error) {
    console.error("subscription checkout error", error);
    toast("Checkout failed. Please try again.");
  }
};
